import AwsS3 from "@uppy/aws-s3";
import Uppy from "@uppy/core";
import { useCallback, useRef, useState } from "react";

import { RECORDER_STATUS } from "../constants";
import { getStatusProperties } from "../helpers";
import { processFileRequest, signedUploadUrlRequest } from "../requests";

export const useWaveSurferRecorder = ({
  recordingId,
}: {
  recordingId: string;
}) => {
  const [status, setStatus] = useState(RECORDER_STATUS.RECORDING);
  const uppy = useRef<Uppy | null>(null);
  const objectIdRef = useRef<string | null>(null);
  const { isRecording, isBusy, isError } = getStatusProperties(status);

  const setServerError = () => setStatus(RECORDER_STATUS.SERVER_ERROR);
  const setUploadError = () => setStatus(RECORDER_STATUS.UPLOAD_ERROR);

  const handleBlob = useCallback(
    (audioBlob: Blob) => {
      setStatus(RECORDER_STATUS.UPLOADING);
      uppy.current = new Uppy();
      uppy.current.use(AwsS3, {
        shouldUseMultipart: false,
        async getUploadParameters(file) {
          try {
            const response = await signedUploadUrlRequest(
              file.data as File,
              setUploadError,
            );
            if (!response) {
              throw new Error("Failed to get upload parameters");
            }
            return response;
          } catch (error) {
            console.error(error);
            return { url: "", error: "Failed to get upload parameters" };
          }
        },
      });

      uppy.current.addFile({
        name: "audio.mp4",
        data: audioBlob,
      });

      void uppy.current.upload();

      uppy.current.on("upload-success", (_file, response) => {
        const objectId = response?.uploadURL?.split(".com/")[1].split("/")[0];
        if (objectId) {
          objectIdRef.current = objectId;
          void processFileRequest(objectId, recordingId, setServerError);
          setStatus(RECORDER_STATUS.PROCESSING);
        } else {
          setUploadError();
        }
      });

      uppy.current.on("upload-error", () => setUploadError());
    },
    [recordingId],
  );

  const onRetry = useCallback(() => {
    if (status === RECORDER_STATUS.UPLOAD_ERROR) {
      setStatus(RECORDER_STATUS.UPLOADING);
      if (uppy.current) {
        void uppy.current.retryAll();
      }
    } else {
      setStatus(RECORDER_STATUS.PROCESSING);
      if (objectIdRef.current) {
        void processFileRequest(objectIdRef.current, recordingId);
      } else {
        setUploadError();
      }
    }
  }, [status, recordingId]);

  return {
    setServerError,
    status,
    isRecording,
    isBusy,
    isError,
    handleBlob,
    onRetry,
  };
};
